import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, X, Pencil, Trash2, Star } from 'lucide-react';

const STATUS_COLOR = {
  approved:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending:     'bg-amber-50 text-amber-700 border-amber-200',
  blacklisted: 'bg-red-50 text-red-700 border-red-200',
};

const blank = () => ({
  name: '', service_type: '', contact_person: '', phone: '',
  email: '', address: '', gst_no: '', pan_no: '', payment_terms: '',
  performance_rating: '', notes: '',
});

export default function AdminVendors() {
  const [items, setItems]   = useState([]);
  const [lookup, setLookup] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(blank());
  const [saving, setSaving]     = useState(false);
  const [selected, setSelected] = useState(null);
  const [svcFilter, setSvcFilter] = useState('');

  const load = () => {
    const q = svcFilter ? `?service_type=${encodeURIComponent(svcFilter)}` : '';
    apiGet(`/admin/vendors${q}`).then(r => setItems(r.items || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [svcFilter]);
  useEffect(() => { apiGet('/admin/lookup').then(setLookup).catch(() => {}); }, []);

  const openNew  = ()  => { setEditing(null); setForm(blank()); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, service_type: r.service_type, contact_person: r.contact_person || '',
      phone: r.phone || '', email: r.email || '', address: r.address || '',
      gst_no: r.gst_no || '', pan_no: r.pan_no || '', payment_terms: r.payment_terms || '',
      performance_rating: r.performance_rating || '', notes: r.notes || '' });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.service_type) { toast.error('Vendor Name and Service Type required'); return; }
    setSaving(true);
    // Convert empty string numeric fields to null
    const payload = {
      ...form,
      performance_rating: form.performance_rating === '' ? null : Number(form.performance_rating) || null,
    };
    try {
      if (editing) {
        await apiPut(`/admin/vendors/${editing.id}`, payload);
        toast.success('Vendor updated');
      } else {
        await apiPost('/admin/vendors', payload);
        toast.success('Vendor added');
      }
      setShowForm(false); load();
    } catch { toast.error('Could not save'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try { await apiDel(`/admin/vendors/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const svcs = lookup.vendor_services || [];

  return (
    <div data-testid="admin-vendors" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-1">Administration</div>
          <h1 className="font-display text-2xl font-extrabold">Admin Vendor Directory</h1>
          <p className="text-sm text-slate-500 mt-0.5">Housekeeping, catering, pest control, courier and other admin service vendors.</p>
        </div>
        <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="vendor-add-btn">
          <Plus size={15} /> Add Vendor
        </button>
      </div>

      {/* Service filter chips */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setSvcFilter('')}
          className={`px-3 py-1 text-xs font-display font-bold uppercase tracking-wider rounded-sm border ${!svcFilter ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-200'}`}>
          All Services
        </button>
        {svcs.map(s => (
          <button key={s} onClick={() => setSvcFilter(s === svcFilter ? '' : s)}
            className={`px-3 py-1 text-xs font-display font-bold uppercase tracking-wider rounded-sm border ${svcFilter === s ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-200'}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.length === 0 && (
          <div className="col-span-3 text-center text-sm text-slate-400 py-12">No vendors added yet</div>
        )}
        {items.map(v => (
          <div key={v.id} data-testid={`vendor-card-${v.id}`}
            className="bg-white border border-slate-200 rounded-sm p-4 hover:shadow-sm cursor-pointer transition-shadow flex flex-col gap-2"
            onClick={() => setSelected(v)}>
            <div className="flex items-start justify-between">
              <span className={`tag text-[10px] border ${STATUS_COLOR[v.status] || ''}`}>{v.service_type}</span>
              {v.performance_rating && (
                <span className="flex items-center gap-0.5 text-amber-500 text-xs font-bold">
                  <Star size={11} fill="currentColor" /> {v.performance_rating}
                </span>
              )}
            </div>
            <h3 className="font-display font-bold text-sm">{v.name}</h3>
            {v.contact_person && <div className="text-xs text-slate-600">{v.contact_person}</div>}
            {v.phone && <div className="text-xs text-slate-500">{v.phone}</div>}
            <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-100">
              <span className={`tag text-[9px] border ${STATUS_COLOR[v.status] || ''}`}>{v.status}</span>
              <div className="flex gap-2">
                <button onClick={e => { e.stopPropagation(); openEdit(v); }} className="text-slate-400 hover:text-[#1F7A6E]" data-testid={`vendor-edit-${v.id}`}><Pencil size={13} /></button>
                <button onClick={e => { e.stopPropagation(); remove(v.id); }} className="text-red-400 hover:text-red-600" data-testid={`vendor-del-${v.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Vendor detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md p-6 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <span className={`tag text-[10px] border ${STATUS_COLOR[selected.service_type]}`}>{selected.service_type}</span>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <h2 className="font-display font-bold text-xl">{selected.name}</h2>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              {selected.contact_person && <><span className="text-slate-500">Contact</span><span className="font-semibold">{selected.contact_person}</span></>}
              {selected.phone && <><span className="text-slate-500">Phone</span><span>{selected.phone}</span></>}
              {selected.email && <><span className="text-slate-500">Email</span><span className="truncate">{selected.email}</span></>}
              {selected.gst_no && <><span className="text-slate-500">GST</span><span>{selected.gst_no}</span></>}
              {selected.pan_no && <><span className="text-slate-500">PAN</span><span>{selected.pan_no}</span></>}
              {selected.payment_terms && <><span className="text-slate-500">Payment</span><span>{selected.payment_terms}</span></>}
            </div>
            {selected.address && <div className="text-xs text-slate-600 border-t pt-2">{selected.address}</div>}
            {selected.notes && <div className="text-xs text-slate-600 italic">{selected.notes}</div>}
          </div>
        </div>
      )}

      {/* Form Drawer */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md bg-white h-full shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
            <header className="px-5 py-4 border-b flex items-center justify-between bg-[#1F7A6E] text-white">
              <h2 className="font-display font-bold text-sm uppercase tracking-wider">{editing ? 'Edit Vendor' : 'Add Vendor'}</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </header>
            <form onSubmit={save} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="label">Vendor Name *</label>
                <input className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="vendor-name" />
              </div>
              <div>
                <label className="label">Service Type *</label>
                <select className="input" value={form.service_type} onChange={e => setForm({...form, service_type: e.target.value})}>
                  <option value="">Select</option>
                  {svcs.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Contact Person</label>
                  <input className="input" value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input className="input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="label">Email</label>
                <input type="email" className="input" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
              <div>
                <label className="label">Address</label>
                <textarea className="input h-16 resize-none" value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">GST No.</label>
                  <input className="input" value={form.gst_no} onChange={e => setForm({...form, gst_no: e.target.value})} />
                </div>
                <div>
                  <label className="label">PAN No.</label>
                  <input className="input" value={form.pan_no} onChange={e => setForm({...form, pan_no: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Payment Terms</label>
                  <input className="input" value={form.payment_terms} onChange={e => setForm({...form, payment_terms: e.target.value})} placeholder="e.g. Net 30" />
                </div>
                <div>
                  <label className="label">Performance Rating</label>
                  <input type="number" min="1" max="5" step="0.5" className="input" value={form.performance_rating} onChange={e => setForm({...form, performance_rating: e.target.value})} placeholder="1 - 5" />
                </div>
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea className="input h-16 resize-none" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
            </form>
            <footer className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="vendor-save">{saving ? 'Saving…' : (editing ? 'Update' : 'Add Vendor')}</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
